import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/Icon";
import { Bluelagoon } from "@bluelagoon";

export default function ConnectCommunityIntegrationModal({ integration, onClose, onConnected }) {
    const { t } = useTranslation();
    const [credentials, setCredentials] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fields = integration?.auth?.credentialFields || [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const res = await Bluelagoon.connectCommunityIntegration({
                communityIntegrationId: integration.id,
                credentials,
            });
            const data = res?.data?.data || res?.data;
            if (data?.error) { setError(data.message || data.error); setLoading(false); return; }
            onConnected?.(data?.integration);
            onClose();
        } catch (err) {
            setError(err?.response?.data?.data?.message || err?.message || t("integrations.community.connectError"));
            setLoading(false);
        }
    };

    const handleChange = (name, value) => setCredentials((prev) => ({ ...prev, [name]: value }));

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <div>
                        <h2 className="font-semibold text-gray-900">{t("integrations.community.connectTitle")}</h2>
                        <p className="text-sm text-gray-500">{integration?.name}</p>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md">
                        <Icon name="X" className="w-5 h-5 text-gray-400" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                    {fields.length === 0 && <p className="text-sm text-gray-500">{t("integrations.community.noCredentials")}</p>}
                    {fields.map((f) => (
                        <div key={f.name}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                            {f.description && <p className="text-xs text-gray-400 mb-1">{f.description}</p>}
                            <input
                                type={f.type || "text"}
                                required={f.required}
                                value={credentials[f.name] || ""}
                                onChange={(e) => handleChange(f.name, e.target.value)}
                                className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={f.label}
                            />
                        </div>
                    ))}
                    {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">{error}</p>}
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                            {t("common.cancel")}
                        </button>
                        <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                            {loading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                            {t("integrations.community.connect")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
